import React from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { BORDER, FOCUS_RING, INK, SHADOW_CARD, SURFACE } from '../../pages/Practice/map/mapTokens';
import { BRAND_NAVY, CARD_CAP, CONTENT_MAX_WIDTH, SOFT_BLUE } from '../../styles/tokens';
import { START_HERE } from './homeContent';
import { SectionHeading, bandSx } from './homeUi';
import { AnimatedIcon } from './AnimatedIcons';

/**
 * The homepage's one decision point, directly beneath the hero.
 *
 * The three doors scroll to the matching chapter rather than leaving the page.
 * Each chapter then makes its own case and carries its own button into the
 * product, so a nervous first-timer reads before committing — and the hero still
 * holds the direct "start practising" route for anyone who wants to skip that.
 *
 * Because these are in-page jumps rather than product entries, they are styled as
 * quiet links with a downward chevron rather than as filled buttons. Three solid
 * navy buttons implied "this takes you somewhere else", which was both misleading
 * and the largest block of blue on the page.
 */
const StartHere = ({ tone }) => {
  const handleJump = (event, targetId) => {
    const target = typeof document !== 'undefined' ? document.getElementById(targetId) : null;
    if (!target) return; // Fall back to the plain #hash if the section is absent.

    event.preventDefault();
    const prefersReduced =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
    // Move focus as well as the viewport, so the jump works for keyboard and
    // screen-reader users. -1 keeps the section out of the tab order afterwards.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  return (
    <Box component="section" sx={bandSx(tone)}>
      <SectionHeading title={START_HERE.title} subtitle={START_HERE.subtitle} />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {START_HERE.cards.map((card) => (
          <Box
            key={card.key}
            component="a"
            href={`#${card.target}`}
            onClick={(event) => handleJump(event, card.target)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              p: { xs: 2.5, md: 3 },
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderTop: `4px solid ${CARD_CAP}`,
              borderRadius: '16px',
              boxShadow: SHADOW_CARD,
              textDecoration: 'none',
              transition: 'transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 22px 34px -16px rgba(19,62,135,.35)',
              },
              '&:hover .startHere-chevron': { transform: 'translateY(3px)' },
              // Hovering anywhere on the card fills its button, so the panel and
              // its action behave as one target.
              '&:hover .startHere-cta': { bgcolor: SOFT_BLUE },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
                '&:hover .startHere-chevron': { transform: 'none' },
              },
              ...FOCUS_RING,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                bgcolor: SOFT_BLUE,
                color: BRAND_NAVY,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <AnimatedIcon name={card.anim} size={30} />
            </Box>

            <Typography variant="h6" component="h3" sx={{ color: 'primary.main', fontWeight: 800 }}>
              {card.title}
            </Typography>
            <Typography sx={{ color: INK, fontSize: '0.9375rem', lineHeight: 1.65, flexGrow: 1, mb: 0.5 }}>
              {card.text}
            </Typography>

            {/* An outlined pill, not a text link and not a solid fill. As bare
                text these read as captions and were missed entirely; as three
                solid navy blocks they were the heaviest colour on the page. The
                outline gives an unmistakable button edge with almost no fill. */}
            <Box
              component="span"
              className="startHere-cta"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                width: '100%',
                py: 1.1,
                px: 2,
                mt: 0.5,
                borderRadius: '999px',
                border: '1.5px solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.9375rem',
                transition: 'background-color .2s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              {card.cta}
              <KeyboardArrowDown
                className="startHere-chevron"
                aria-hidden
                sx={{
                  fontSize: 20,
                  transition: 'transform .2s ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StartHere;
